import { Link } from 'react-router-dom';
import { Check, Star, ArrowRight, Brain, User, Clock, Skull, Circle, Cpu, Mic, Calendar, Ghost, Lock } from 'lucide-react';
import { Room } from '@/types/room';
import { useProgression } from '@/hooks/useProgression';
import { useLanguage } from '@/i18n/LanguageContext';
import BookmarkButton from './BookmarkButton';

interface RoomCardProps {
  room: Room;
  variant?: 'default' | 'compact' | 'featured';
  animationDelay?: number;
  className?: string;
  showBookmark?: boolean;
}

const categoryConfig: Record<string, { border: string; glow: string; icon: typeof Brain }> = {
  truth: { border: 'border-l-blue-500/40', glow: 'hover:shadow-blue-500/10', icon: Brain },
  identity: { border: 'border-l-purple-500/40', glow: 'hover:shadow-purple-500/10', icon: User },
  time: { border: 'border-l-amber-500/40', glow: 'hover:shadow-amber-500/10', icon: Clock },
  mortality: { border: 'border-l-rose-500/40', glow: 'hover:shadow-rose-500/10', icon: Skull },
  nothing: { border: 'border-l-slate-400/40', glow: 'hover:shadow-slate-400/10', icon: Circle },
  meta: { border: 'border-l-cyan-500/40', glow: 'hover:shadow-cyan-500/10', icon: Cpu },
  voice: { border: 'border-l-emerald-500/40', glow: 'hover:shadow-emerald-500/10', icon: Mic },
  daily: { border: 'border-l-blue-400/40', glow: 'hover:shadow-blue-400/10', icon: Calendar },
  scary: { border: 'border-l-red-500/40', glow: 'hover:shadow-red-500/10', icon: Ghost },
  secret: { border: 'border-l-violet-500/40', glow: 'hover:shadow-violet-500/10', icon: Lock },
};

const RoomCard = ({ 
  room, 
  variant = 'default', 
  animationDelay = 0,
  className = '',
  showBookmark = true
}: RoomCardProps) => {
  const { isRoomCompleted, state } = useProgression();
  const { t } = useLanguage();
  
  const isCompleted = isRoomCompleted(room.id);
  const isBossRoom = room.id.startsWith('boss-');
  const isNew = !state.completedRooms.includes(room.id) && state.completedRooms.length > 0;
  const config = categoryConfig[room.category] || { border: 'border-l-foreground/20', glow: '', icon: Circle };
  const CategoryIcon = config.icon;

  const getTitle = () => {
    const key = `room_${room.id}`;
    const translated = t(key);
    return translated !== key ? translated : room.title;
  };

  const getSubtitle = () => {
    const key = `sub_${room.id}`;
    const translated = t(key);
    return translated !== key ? translated : room.subtitle;
  };

  if (variant === 'compact') {
    return (
      <Link
        to={room.path}
        className={`group flex items-center gap-3 p-3 border border-border/10 rounded-xl bg-card/20 hover:bg-card/40 hover:border-border/30 transition-all duration-200 active:scale-[0.98] ${className}`}
        style={{ animationDelay: `${animationDelay}ms` }}
      >
        {isCompleted && <Check className="w-3 h-3 text-foreground/50" />}
        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors duration-200">
          {getTitle()}
        </span>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={room.path}
        className={`group relative block overflow-hidden rounded-xl border-2 transition-all duration-300 ${
          isBossRoom 
            ? 'border-foreground/20 bg-gradient-to-br from-foreground/5 via-transparent to-transparent hover:border-foreground/40'
            : 'border-border/20 hover:border-foreground/30 bg-card/30 hover:bg-card/50'
        } ${className}`}
        style={{ animationDelay: `${animationDelay}ms` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <div className="relative p-6 md:p-8">
          <div className="flex items-start justify-between mb-3">
            <span className="text-[10px] text-muted-foreground/30 uppercase tracking-widest font-medium">
              {room.category}
            </span>
            <div className="flex items-center gap-2">
              {isNew && !isCompleted && (
                <Star className="w-3 h-3 text-amber-400/60 animate-pulse-slow" />
              )}
              {isCompleted && (
                <div className="w-2 h-2 rounded-full bg-emerald-500/40" />
              )}
            </div>
          </div>
          <h3 className="text-base md:text-lg font-light text-foreground/90 group-hover:text-foreground transition-colors duration-200 mb-2">
            {getTitle()}
          </h3>
          <p className="text-muted-foreground/50 text-sm leading-relaxed">
            {getSubtitle()}
          </p>
          
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-1 group-hover:translate-x-0">
            <ArrowRight className="w-4 h-4 text-muted-foreground/40" />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={room.path}
      className={`group relative overflow-hidden rounded-xl border border-border/10 border-l-[3px] ${config.border} bg-card/20 hover:bg-card/40 transition-all duration-300 active:scale-[0.98] hover:-translate-y-1 hover:shadow-lg ${config.glow} ${className}`}
      style={{ 
        animationDelay: `${animationDelay}ms`,
        animation: 'fadeIn 0.4s ease-out forwards',
        opacity: 0 
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-foreground/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative p-5">
        <div className="flex items-start justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <CategoryIcon className="w-3.5 h-3.5 text-muted-foreground/25 group-hover:text-muted-foreground/50 transition-colors" />
            <span className="text-[10px] text-muted-foreground/25 uppercase tracking-widest">
              {room.category}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {showBookmark && (
              <BookmarkButton roomId={room.id} size="sm" />
            )}
            {isNew && !isCompleted && (
              <Star className="w-3 h-3 text-amber-400/50" />
            )}
            {isCompleted && (
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/50" title="Completed" />
            )}
          </div>
        </div>
        <h3 className="font-light text-foreground/80 group-hover:text-foreground transition-colors duration-200 text-sm leading-snug">
          {getTitle()}
        </h3>
        <p className="text-muted-foreground/40 text-xs mt-1.5 line-clamp-2 leading-relaxed">
          {getSubtitle()}
        </p>
      </div>
    </Link>
  );
};

export default RoomCard;
